import React from 'react';
import PropTypes from 'prop-types';
import { withTheme } from 'styled-components';
import { ArrowForwardIcon } from '@rsos/assets/icons';
import { Box, Text } from '@rsos/components/capstone/base';
import { DISPATCH_REQUESTED, NEW } from '@rsos/constants/alertStatuses';
import { NOTIFIED_AGENCY } from '@rsos/constants/alertsRoutingConstants';
import { getTheme } from '@rsos/theme/themes';
import isLocationRestricted from '@rsos/utils/isLocationRestricted';
import { displayMilitaryTime } from '@rsos/utils/timeHelpers';
import {
  Address,
  Container,
  Icon,
  IconWrapper,
  InfoWrapper,
  Name,
  Status,
  Time,
  TimeWrapper,
  Type,
  EmergencyType,
  SubType,
} from './Alert.styles';
import { displayAlertStatus, getDisplayAddress, mapIcon } from './utils';

const DEFAULT_ALERT = {
  emergency_type: {
    name: '',
    display_name: '',
  },
  site_type: {
    name: '',
    display_name: '',
  },
  status: {
    name: '',
    display_name: '',
  },
};

/**
 *
 * @param {object} alert - information about the alert
 * @param {boolean} isMapMarker - is the alert being displayed on the map?
 * @param {String} isMapMarkerHovered - flag for whether the corresponding
 * marker is hovered on the map
 * @param {boolean} isSelected - is the alert selected?
 * @param {String} userProfile - profile of current user
 */
const Alert = ({
  alert = DEFAULT_ALERT,
  isMapMarker = false,
  isMapMarkerHovered,
  isSelected,
  userProfile,
  theme = getTheme('light'),
}) => {
  const ACTIVE_ASSAILANT = 'ACTIVE_ASSAILANT';
  const {
    alert_id,
    emergency_type = {},
    incident_time,
    location = {},
    isStatusUpdated,
    isUnread,
    site_type = {},
    status = {},
    supplemental_only: isSupplementalOnly,
    associated_role_type,
    subtype,
  } = alert;
  const { civic = {} } = location;
  const { name, city, state, zip_code } = civic;
  const isActiveAssailant =
    emergency_type?.name === ACTIVE_ASSAILANT &&
    alert.status.name === DISPATCH_REQUESTED;

  const isRestricted = isLocationRestricted(alert);

  const alertStatus = status.name;

  const alertResponseBy = status.sender
    ? status.sender.replace(/\s/g, '')?.toLowerCase()
    : '';
  const fullName = userProfile?.first_name + userProfile?.last_name || '';
  const user =
    JSON.stringify(userProfile) !== '{}' ? fullName.toLowerCase() : '';
  const whoTookAction =
    alertResponseBy && user
      ? alertResponseBy === user
        ? status.display_name + ' BY YOU'
        : status.display_name + ' BY ' + status.sender
      : status.display_name;
  const coveringPsapName = alert?.covering_psap?.name;
  return (
    <Container
      isHovered={isMapMarkerHovered}
      isMapMarker={isMapMarker}
      isSupplementalOnly={isSupplementalOnly}
      isSelected={isSelected}
      isStatusUpdated={isStatusUpdated}
      isUnread={isUnread}
      status={alertStatus}
      theme={theme}
      data-name={alert_id}
    >
      <IconWrapper>
        {emergency_type.name && (
          <Icon data-name="alert-icon">
            {mapIcon(emergency_type.name, theme)}
          </Icon>
        )}
        {site_type.display_name && (
          <Type isActiveAssailant={isActiveAssailant} theme={theme}>
            {site_type.display_name.substring(0, 3)}
          </Type>
        )}
      </IconWrapper>
      <InfoWrapper>
        <>
          <EmergencyType
            theme={theme}
            data-name="emergency-type"
            isActiveAssailant={isActiveAssailant}
          >
            {emergency_type.display_name}
          </EmergencyType>
          {subtype && <SubType data-name="alert-subtype">{subtype}</SubType>}
          <Name
            theme={theme}
            isActiveAssailant={isActiveAssailant}
            data-name="alert-name"
          >
            {name}
          </Name>
          <Address
            theme={theme}
            isActiveAssailant={isActiveAssailant}
            data-name="alert-address-1"
          >
            {getDisplayAddress({ isLocationRestricted: isRestricted, civic })}
          </Address>
          <Address
            theme={theme}
            isActiveAssailant={isActiveAssailant}
            data-name="alert-address-2"
          >
            {city}
            {state && <>, {state}</>} {zip_code}
          </Address>
        </>
        {associated_role_type === NOTIFIED_AGENCY ? (
          <Box horizontal flow={5}>
            <Status status={alertStatus} theme={theme} data-name="alert-status">
              <Box horizontal flow={5} align="flex-start">
                <ArrowForwardIcon width={14} height={12} />
                <Text>
                  {alertStatus === DISPATCH_REQUESTED
                    ? NEW + ' - ' + coveringPsapName
                    : status.display_name + ' - ' + coveringPsapName}
                </Text>
              </Box>
            </Status>
          </Box>
        ) : (
          <Status status={alertStatus} theme={theme} data-name="alert-status">
            {displayAlertStatus(isSupplementalOnly, alertStatus, whoTookAction)}
          </Status>
        )}
      </InfoWrapper>

      {incident_time && (
        <TimeWrapper>
          <Time theme={theme} data-name="alert-time">
            {displayMilitaryTime(incident_time)}
          </Time>
        </TimeWrapper>
      )}
    </Container>
  );
};

Alert.propTypes = {
  alert: PropTypes.shape({
    alert_id: PropTypes.string,
    emergency_type: PropTypes.shape({
      name: PropTypes.string,
      display_name: PropTypes.string,
    }),
    followers: PropTypes.arrayOf(PropTypes.string),
    incident_time: PropTypes.number.isRequired,
    isStatusUpdated: PropTypes.bool,
    isUnread: PropTypes.bool,
    location: PropTypes.shape({
      civic: PropTypes.shape({
        name: PropTypes.string,
        street_1: PropTypes.string,
        street_2: PropTypes.string,
        city: PropTypes.string,
        state: PropTypes.string,
        zip_code: PropTypes.string,
      }),
    }).isRequired,
    site_type: PropTypes.shape({
      name: PropTypes.string,
      display_name: PropTypes.string,
    }),
    status: PropTypes.shape({
      name: PropTypes.string,
      display_name: PropTypes.string,
    }),
    supplemental_only: PropTypes.bool,
  }),
  isMapMarker: PropTypes.bool,
  isMapMarkerHovered: PropTypes.bool,
  isSelected: PropTypes.bool,
  userProfile: PropTypes.object,
  subtype: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
};

export default withTheme(Alert);
